import { useQueryClient, useMutation } from '@tanstack/react-query';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import {
  NodeSelectionTemplateSummary,
  UpdateSelectionTemplate,
  UpdateNodeTemplateRequest,
  CreateNodeTemplateRequest,
  CreateSelectionTemplate,
} from '@cast/types';

import { getGeneratedTemplatesQueryKey } from 'hooks/queries/autoscaler/useGeneratedTemplatesQuery';
import { getSelectionTemplatesQueryKey } from 'hooks/queries/autoscaler/useSelectionTemplatesQuery';
import { getClusterEdgeLocationsQueryKey } from 'hooks/queries/omni/useGetClusterEdgeLocationsQuery';

type UseTurnOnAutoscalingPayload = {
  clusterId: string;
  selectionTemplates?: NodeSelectionTemplateSummary[];
};

const turnOnNodeAutoscaling = async ({
  clusterId,
  selectionTemplates = [],
}: UseTurnOnAutoscalingPayload) => {
  const toUpdateRequest = ({
    template,
  }: NodeSelectionTemplateSummary): UpdateNodeTemplateRequest | undefined => {
    if (!template?.name) {
      return;
    }
    return {
      clusterId,
      nodeTemplateName: template.name,
      nodeTemplate: template as UpdateSelectionTemplate,
    };
  };

  const toCreateRequest = ({
    template,
  }: NodeSelectionTemplateSummary): CreateNodeTemplateRequest | undefined => {
    if (!template) {
      return;
    }
    return {
      clusterId,
      nodeTemplate: template as CreateSelectionTemplate,
    };
  };

  const onlyDefaultOrNoTemplates = !selectionTemplates?.filter(
    ({ template }) => !template?.isDefault
  ).length;

  // Generate templates only when there are no templates, or default template alone
  const generatedTemplates =
    (onlyDefaultOrNoTemplates &&
      (
        await apiClient.autoscaler.generateNodeTemplates({
          clusterId,
        })
      ).data?.items) ||
    [];

  // Enable existing templates, and update them with the content of generated templates
  const templatesToUpdate = selectionTemplates
    .map((template) => {
      const generatedTemplate =
        template.template?.version === '0'
          ? generatedTemplates.find(
              ({ template: generatedTemplate }) =>
                generatedTemplate?.name &&
                template.template?.name &&
                generatedTemplate.name === template.template.name
            )
          : undefined;

      const templateToUpdate = cloneDeep(generatedTemplate || template);
      if (!templateToUpdate.template?.isEnabled) {
        set(templateToUpdate, 'template.isEnabled', true);
      }
      if (isEqual(template, templateToUpdate)) {
        return;
      }
      return toUpdateRequest(templateToUpdate);
    })
    .filter((template) => !!template);

  // If the generated template does not have an existing template, create it.
  const templatesToCreate =
    generatedTemplates
      .filter(
        (generatedTemplate) =>
          !selectionTemplates.some(
            ({ template }) =>
              template?.name === generatedTemplate.template?.name
          )
      )
      .map(toCreateRequest)
      .filter((request) => !!request) || [];

  const updatePromises = templatesToUpdate.map((updateRequest) =>
    apiClient.autoscaler.updateSelectionTemplate(updateRequest)
  );

  const createPromises = templatesToCreate.map((createRequest) =>
    apiClient.autoscaler.createSelectionTemplate(createRequest)
  );

  await Promise.all([...updatePromises, ...createPromises]);
};

export const useTurnOnNodeAutoscalingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: turnOnNodeAutoscaling,
    onSuccess: (_, { clusterId }) => {
      queryClient.invalidateQueries({
        queryKey: getSelectionTemplatesQueryKey(clusterId),
      });
      queryClient.invalidateQueries({
        queryKey: getClusterEdgeLocationsQueryKey({ clusterId }),
      });
      queryClient.invalidateQueries({
        queryKey: getGeneratedTemplatesQueryKey(clusterId),
      });
    },
    onError: (error) => {
      notify.error(parseApiError(error));
    },
  });
};
